var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Incident Tiles Component
 * Displays incident information in horizontally scrollable tiles
 */
import { DefaultButton, Dialog, DialogFooter, DialogType, Icon, Link, MessageBar, MessageBarType, Spinner, SpinnerSize, Stack, Text, TextField, } from "@fluentui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchOutagesByTpid, formatImpactStartTime, generateIcmPortalUrl, } from "./actions";
import { incidentTilesStyles } from "./styles/IncidentTiles.styles";
const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
        case "active":
            return incidentTilesStyles.statusActive;
        case "investigating":
            return incidentTilesStyles.statusInvestigating;
        case "mitigated":
            return incidentTilesStyles.statusMitigated;
        case "monitoring":
            return incidentTilesStyles.statusMonitoring;
        case "resolved":
            return incidentTilesStyles.statusResolved;
        default:
            return incidentTilesStyles.statusActive;
    }
};
const getSeverityBadgeClass = (severity) => {
    switch (severity.toLowerCase()) {
        case "error":
        case "sev 0":
        case "sev 1":
            return incidentTilesStyles.severityError;
        case "warning":
        case "sev 2":
            return incidentTilesStyles.severityWarning;
        case "informational":
        case "sev 3":
        case "sev 4":
        case "sev 5":
        default:
            return incidentTilesStyles.severityInformational;
    }
};
const ITEMS_PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 400;
const MAX_PAGE_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 400;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// Stable reference for the default selection so omitting the prop does not
// create a new array on every render (which would re-fire the effect that
// syncs selection into local state and cause an infinite render loop).
const EMPTY_SELECTION = [];
export const IncidentTiles = ({ customerTpid, isVisible, selectedIncidents = EMPTY_SELECTION, onSelectionChange, onIncidentCountChange, onIncidentsLoaded, onCustomerLookupCompleted, }) => {
    var _a;
    const [incidents, setIncidents] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [internalSelectedIds, setInternalSelectedIds] = useState(selectedIncidents);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(false);
    const [regionsDialogIncident, setRegionsDialogIncident] = useState(null);
    const scrollContainerRef = useRef(null);
    const searchDebounceRef = useRef(null);
    // Monotonically increasing id used to supersede in-flight requests so that a
    // stale response for a previous TPID/search cannot overwrite current state.
    const requestIdRef = useRef(0);
    const incidentsRef = useRef([]);
    const isMountedRef = useRef(true);
    useEffect(() => {
        incidentsRef.current = incidents;
    }, [incidents]);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const loadIncidents = useCallback((page, search, append = false) => __awaiter(void 0, void 0, void 0, function* () {
        if (!customerTpid.trim())
            return;
        const normalizedSearch = search.trim();
        // Supersede any in-flight request; only the latest request is allowed to
        // commit results or clear loading state.
        const requestId = ++requestIdRef.current;
        if (append) {
            setIsLoadingMore(true);
        }
        else {
            setIsInitialLoading(true);
            // A non-append load supersedes any in-flight append; clear its loading
            // flag so a superseded append cannot leave load-more/paging stuck.
            setIsLoadingMore(false);
        }
        setError(null);
        // A page can fail transiently (e.g. a spurious 404 from the backend that
        // succeeds on a subsequent call). Retry a bounded number of times with a
        // short backoff before giving up so auto-paging can complete without
        // looping indefinitely on the same page.
        const maxAttempts = MAX_PAGE_RETRIES + 1;
        let lastError = null;
        try {
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    const previouslyLoadedCount = append
                        ? incidentsRef.current.length
                        : 0;
                    const { items, totalCount: count } = yield fetchOutagesByTpid({
                        tpid: customerTpid,
                        page,
                        pageSize: ITEMS_PER_PAGE,
                        search: normalizedSearch || undefined,
                        orderBy: "impactStartTime desc",
                    });
                    if (!isMountedRef.current || requestId !== requestIdRef.current) {
                        return;
                    }
                    const mergedItems = append
                        ? [...incidentsRef.current, ...items]
                        : items;
                    setIncidents(mergedItems);
                    setTotalCount(count);
                    setCurrentPage(page);
                    // Treat a zero-item page as terminal: if the API returns no items
                    // while still reporting totalCount > loaded (off-by-one paging,
                    // filtered results, cache staleness, backend bug), never mark more
                    // pages available — otherwise the auto-load effect would request the
                    // next page forever, one HTTP request per commit.
                    setHasMorePages(items.length > 0 && previouslyLoadedCount + items.length < count);
                    if (onIncidentCountChange) {
                        onIncidentCountChange(count);
                    }
                    if (onIncidentsLoaded) {
                        onIncidentsLoaded(mergedItems);
                    }
                    if (onCustomerLookupCompleted &&
                        !append &&
                        page === 1 &&
                        !normalizedSearch) {
                        onCustomerLookupCompleted(count > 0 || items.length > 0);
                    }
                    return;
                }
                catch (err) {
                    lastError = err;
                    if (attempt < maxAttempts) {
                        yield delay(RETRY_BASE_DELAY_MS * attempt);
                    }
                }
            }
            // Every attempt for this page failed — stop auto-paging so the same
            // page is not requested again, and surface the failure to the user.
            if (!isMountedRef.current || requestId !== requestIdRef.current) {
                return;
            }
            console.error("Error fetching incidents:", lastError);
            setHasMorePages(false);
            setError(append
                ? "Could not load more incident data. Showing the incidents already loaded."
                : "Failed to load incident data");
            if (!append && onIncidentCountChange) {
                onIncidentCountChange(0);
            }
        }
        finally {
            // Only the latest request may clear the loading state; a superseded
            // request must not flip loading flags for the current request.
            if (requestId === requestIdRef.current && isMountedRef.current) {
                if (append) {
                    setIsLoadingMore(false);
                }
                else {
                    setIsInitialLoading(false);
                }
            }
        }
    }), [
        customerTpid,
        onCustomerLookupCompleted,
        onIncidentCountChange,
        onIncidentsLoaded,
    ]);
    // Initial fetch when component becomes visible or customer TPID changes.
    useEffect(() => {
        if (isVisible && customerTpid.trim()) {
            setCurrentPage(1);
            setSearchQuery("");
            setHasMorePages(false);
            // Reset the previous customer's total so hasLoadedData
            // (totalCount > 0 || incidents.length > 0) is false while the new
            // customer's first page is in flight — otherwise the initial loading
            // spinner is skipped and a stale "no incidents" empty state flashes.
            setTotalCount(0);
            // Cancel any pending debounced search from the previous customer so its
            // stale request cannot supersede this customer's load and commit the
            // wrong customer's incidents.
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = null;
            }
            loadIncidents(1, "", false);
        }
    }, [isVisible, customerTpid]);
    // Debounced search
    const handleSearchChange = (_ev, value) => {
        const newQuery = value !== null && value !== void 0 ? value : "";
        setSearchQuery(newQuery);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            setCurrentPage(1);
            setHasMorePages(false);
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollLeft = 0;
            }
            loadIncidents(1, newQuery, false);
        }, SEARCH_DEBOUNCE_MS);
    };
    // Auto-load remaining pages, but stop if a page fails so the same page is not retried in a loop.
    useEffect(() => {
        if (!isVisible ||
            isInitialLoading ||
            isLoadingMore ||
            !hasMorePages ||
            error) {
            return;
        }
        loadIncidents(currentPage + 1, searchQuery, true);
    }, [
        currentPage,
        error,
        hasMorePages,
        isInitialLoading,
        isLoadingMore,
        isVisible,
        loadIncidents,
        searchQuery,
    ]);
    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);
    useEffect(() => {
        setInternalSelectedIds(selectedIncidents);
    }, [selectedIncidents]);
    const handleIncidentSelection = (incidentId) => {
        let newSelectedIds;
        const isCurrentlySelected = internalSelectedIds.includes(incidentId);
        if (isCurrentlySelected) {
            newSelectedIds = internalSelectedIds.filter((id) => id !== incidentId);
        }
        else {
            newSelectedIds = [...internalSelectedIds, incidentId];
        }
        setInternalSelectedIds(newSelectedIds);
        if (onSelectionChange) {
            onSelectionChange(newSelectedIds);
        }
    };
    const openRegionsDialog = (e, incident) => {
        e.preventDefault();
        e.stopPropagation();
        setRegionsDialogIncident({
            icmNumber: incident.icmNumber,
            title: incident.title,
            regions: incident.regionsImpacted,
        });
    };
    const closeRegionsDialog = () => {
        setRegionsDialogIncident(null);
    };
    const hasLoadedData = totalCount > 0 || incidents.length > 0;
    if (!isVisible) {
        return null;
    }
    // Full-page spinner only for the very first load (no data fetched yet)
    if (isInitialLoading && !hasLoadedData) {
        return (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingContainer }, { children: _jsxs(Stack, Object.assign({ horizontalAlign: "center", tokens: { childrenGap: 16 } }, { children: [_jsx(Spinner, { size: SpinnerSize.large }), _jsx(Text, { children: "Loading incident data..." })] })) })));
    }
    if (error && !hasLoadedData) {
        return (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingContainer }, { children: _jsxs(Stack, Object.assign({ horizontalAlign: "center", tokens: { childrenGap: 16 } }, { children: [_jsx(Icon, { iconName: "ErrorBadge", style: { fontSize: "24px", color: "#d13438" } }), _jsx(Text, Object.assign({ style: { color: "#d13438" } }, { children: error }))] })) })));
    }
    if (incidents.length === 0 && !searchQuery.trim() && !isInitialLoading) {
        return (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingContainer }, { children: _jsxs(Stack, Object.assign({ horizontalAlign: "center", tokens: { childrenGap: 16 } }, { children: [_jsx(Icon, { iconName: "CheckMark", style: { fontSize: "24px", color: "#32cd32" } }), _jsx(Text, { children: "No incidents found for this customer" })] })) })));
    }
    const hasSearchResults = incidents.length > 0;
    return (_jsxs("div", { children: [_jsxs("div", Object.assign({ className: incidentTilesStyles.toolbar }, { children: [_jsx(TextField, { className: incidentTilesStyles.searchBox, placeholder: "Search by incident/outage number", value: searchQuery, onChange: handleSearchChange }), _jsxs(Text, Object.assign({ className: incidentTilesStyles.pageInfo }, { children: ["Loaded ", incidents.length, " of ", totalCount || incidents.length] }))] })), error && hasLoadedData && (_jsx(MessageBar, Object.assign({ messageBarType: MessageBarType.warning, className: incidentTilesStyles.inlineMessage }, { children: error }))), isInitialLoading ? (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingContainer }, { children: _jsxs(Stack, Object.assign({ horizontalAlign: "center", tokens: { childrenGap: 16 } }, { children: [_jsx(Spinner, { size: SpinnerSize.large }), _jsx(Text, { children: "Loading incident data..." })] })) }))) : error && !hasLoadedData ? (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingContainer }, { children: _jsxs(Stack, Object.assign({ horizontalAlign: "center", tokens: { childrenGap: 16 } }, { children: [_jsx(Icon, { iconName: "ErrorBadge", style: { fontSize: "24px", color: "#d13438" } }), _jsx(Text, Object.assign({ style: { color: "#d13438" } }, { children: error }))] })) }))) : !hasSearchResults ? (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingContainer }, { children: _jsxs(Stack, Object.assign({ horizontalAlign: "center", tokens: { childrenGap: 12 } }, { children: [_jsx(Icon, { iconName: "SearchIssue", style: { fontSize: "24px", color: "#605e5c" } }), _jsxs(Text, { children: ["No incidents/outages match \"", searchQuery.trim(), "\"."] })] })) }))) : (_jsx("div", Object.assign({ ref: scrollContainerRef, className: incidentTilesStyles.scrollContainer }, { children: _jsxs("div", Object.assign({ className: incidentTilesStyles.container }, { children: [incidents.map((incident, index) => {
                            const isSelected = internalSelectedIds.includes(incident.icmNumber);
                            const isOutage = !!incident.isOutage;
                            return (_jsxs("div", Object.assign({ className: `${incidentTilesStyles.tile} ${isOutage ? incidentTilesStyles.tileOutage : ""} ${isSelected ? incidentTilesStyles.tileSelected : ""}`, onClick: () => handleIncidentSelection(incident.icmNumber), role: "checkbox", tabIndex: 0, "aria-checked": isSelected, onKeyDown: (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleIncidentSelection(incident.icmNumber);
                                    }
                                } }, { children: [isSelected && (_jsx("div", Object.assign({ className: incidentTilesStyles.selectedIndicator }, { children: _jsx(Icon, { iconName: "CheckMark", style: { fontSize: "12px", color: "#ffffff" } }) }))), _jsxs("div", Object.assign({ className: incidentTilesStyles.header }, { children: [isOutage && (_jsx("span", Object.assign({ className: incidentTilesStyles.outageBadge }, { children: "OUTAGE" }))), _jsx("div", Object.assign({ className: incidentTilesStyles.headerContent }, { children: _jsxs(Link, Object.assign({ href: generateIcmPortalUrl(incident.icmNumber, !!incident.isOutage), target: "_blank", rel: "noopener noreferrer", className: incidentTilesStyles.icmLink }, { children: [incident.icmNumber, _jsx(Icon, { iconName: "NavigateExternalInline", className: incidentTilesStyles.externalIcon })] })) }))] })), incident.title && (_jsx("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: _jsx(Text, Object.assign({ variant: "mediumPlus", title: incident.title, className: incidentTilesStyles.incidentTitle, style: {
                                                color: isSelected ? "#0078d4" : "#323130",
                                            } }, { children: incident.title })) }))), incident.description && (_jsx("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: _jsx(Text, Object.assign({ variant: "small", style: {
                                                color: "#605e5c",
                                                lineHeight: "1.4",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            } }, { children: incident.description })) }))), _jsxs("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.fieldLabel }, { children: "Status" })), _jsx("div", Object.assign({ className: incidentTilesStyles.fieldValue }, { children: _jsx("span", Object.assign({ className: `${incidentTilesStyles.statusBadge} ${getStatusBadgeClass(incident.status)}` }, { children: incident.status.toUpperCase() })) }))] })), _jsxs("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.fieldLabel }, { children: "Severity" })), _jsx("div", Object.assign({ className: incidentTilesStyles.fieldValue }, { children: _jsx("span", Object.assign({ className: `${incidentTilesStyles.severityBadge} ${getSeverityBadgeClass(incident.severity)}` }, { children: incident.severity.toUpperCase() })) }))] })), _jsxs("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.fieldLabel }, { children: "Impact Type" })), _jsx("div", Object.assign({ className: incidentTilesStyles.fieldValue }, { children: incident.impactType }))] })), _jsxs("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.fieldLabel }, { children: "Impact Start" })), _jsx("div", Object.assign({ className: incidentTilesStyles.fieldValue }, { children: formatImpactStartTime(incident.impactStart) }))] })), _jsxs("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.fieldLabel }, { children: "Regions" })), _jsx("div", Object.assign({ className: incidentTilesStyles.arrayValue }, { children: incident.regionsImpacted.length > 0 ? (_jsxs(_Fragment, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.arrayValueItem }, { children: incident.regionsImpacted[0] })), incident.regionsImpacted.length > 1 && (_jsxs(Link, Object.assign({ onClick: (e) => openRegionsDialog(e, incident), style: { fontSize: "12px", whiteSpace: "nowrap" } }, { children: ["Show more (", incident.regionsImpacted.length - 1, ")"] })))] })) : (_jsx("div", Object.assign({ className: incidentTilesStyles.arrayValueItem }, { children: "N/A" }))) }))] }))] }), `${incident.icmNumber}-${index}`));
                        }), isLoadingMore && (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingMoreTile }, { children: _jsx(Spinner, { size: SpinnerSize.medium, label: "Loading more..." }) })))] })) }))), _jsxs(Dialog, Object.assign({ hidden: !regionsDialogIncident, onDismiss: closeRegionsDialog, dialogContentProps: {
                    type: DialogType.largeHeader,
                    title: regionsDialogIncident
                        ? `Impacted Regions - ${regionsDialogIncident.icmNumber}`
                        : "Impacted Regions",
                    subText: regionsDialogIncident === null || regionsDialogIncident === void 0 ? void 0 : regionsDialogIncident.title,
                }, modalProps: {
                    isBlocking: false,
                } }, { children: [_jsx("div", Object.assign({ style: {
                            maxHeight: "320px",
                            overflowY: "auto",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                        } }, { children: ((_a = regionsDialogIncident === null || regionsDialogIncident === void 0 ? void 0 : regionsDialogIncident.regions) !== null && _a !== void 0 ? _a : []).map((region, idx) => (_jsx("div", Object.assign({ className: incidentTilesStyles.arrayValueItem }, { children: region }), `${region}-${idx}`))) })), _jsx(DialogFooter, { children: _jsx(DefaultButton, { onClick: closeRegionsDialog, text: "Close" }) })] }))] }));
};
export default IncidentTiles;
